import jsPDF from 'jspdf';

export interface MarksheetPDFData {
  userName: string;
  courseTitle: string;
  quizScores: Array<{
    moduleName: string;
    percentage: number;
    passed: boolean;
    attemptDate: string;
    score: number;
    maxScore: number;
  }>;
  overallAverage: number;
  certificateNumber: string;
  completionDate: string;
  studentEmail?: string;
}

/**
 * Generates a professional marksheet PDF with module-by-module breakdown
 * Includes validation to prevent empty PDFs
 */
export async function generateMarksheetPDF(data: MarksheetPDFData): Promise<void> {
  // Validate required data
  if (!data.quizScores || data.quizScores.length === 0) {
    console.warn('[Marksheet Generator] No quiz scores available for marksheet');
    throw new Error('No quiz data available. The student may not have completed any quizzes for this course.');
  }

  // Check for invalid module names (raw IDs instead of titles)
  const hasRawIds = data.quizScores.some(score => 
    score.moduleName.includes('-') && score.moduleName.length > 30
  );
  if (hasRawIds) {
    console.warn('[Marksheet Generator] Some module names appear to be raw IDs instead of titles');
  }

  // Sanitize data to prevent undefined values
  const sanitizedScores = data.quizScores.map(score => ({
    ...score,
    moduleName: score.moduleName || 'Unknown Module',
    percentage: isNaN(score.percentage) ? 0 : score.percentage,
    score: isNaN(score.score) ? 0 : score.score,
    maxScore: isNaN(score.maxScore) ? 1 : score.maxScore,
    attemptDate: score.attemptDate || 'N/A'
  }));

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  // ============ HEADER ============
  // Academy Logo/Name
  pdf.setFontSize(28);
  pdf.setTextColor(0, 100, 180);
  pdf.text('NEO PRISM ACADEMY', pageWidth / 2, yPos, { align: 'center' });

  yPos += 12;
  pdf.setFontSize(18);
  pdf.setTextColor(80, 80, 80);
  pdf.text('ACADEMIC MARKSHEET', pageWidth / 2, yPos, { align: 'center' });

  // Decorative line
  yPos += 8;
  pdf.setDrawColor(0, 100, 180);
  pdf.setLineWidth(1);
  pdf.line(margin, yPos, pageWidth - margin, yPos);

  // ============ STUDENT INFO SECTION ============
  yPos += 15;
  pdf.setFontSize(11);
  pdf.setTextColor(60, 60, 60);

  // Left column
  pdf.setFont('helvetica', 'bold');
  pdf.text('Student Name:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.userName, margin + 35, yPos);

  // Right column
  pdf.setFont('helvetica', 'bold');
  pdf.text('Certificate #:', pageWidth / 2 + 10, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.certificateNumber, pageWidth / 2 + 40, yPos);

  yPos += 8;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Course:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.courseTitle, margin + 35, yPos);

  pdf.setFont('helvetica', 'bold');
  pdf.text('Completion:', pageWidth / 2 + 10, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.completionDate, pageWidth / 2 + 40, yPos);

  if (data.studentEmail) {
    yPos += 8;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Email:', margin, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(data.studentEmail, margin + 35, yPos);
  }

  // ============ MODULE BREAKDOWN SECTION ============
  yPos += 20;
  pdf.setFontSize(14);
  pdf.setTextColor(0, 100, 180);
  pdf.setFont('helvetica', 'bold');
  pdf.text('MODULE BREAKDOWN', margin, yPos);

  // Table header
  yPos += 10;
  const colWidths = {
    module: 80,
    score: 25,
    percentage: 25,
    date: 30,
    status: 20,
  };

  // Header background
  pdf.setFillColor(0, 100, 180);
  pdf.rect(margin, yPos - 5, pageWidth - margin * 2, 8, 'F');

  pdf.setFontSize(9);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');

  let xPos = margin + 3;
  pdf.text('Module Name', xPos, yPos);
  xPos += colWidths.module;
  pdf.text('Score', xPos, yPos);
  xPos += colWidths.score;
  pdf.text('% Score', xPos, yPos);
  xPos += colWidths.percentage;
  pdf.text('Date', xPos, yPos);
  xPos += colWidths.date;
  pdf.text('Status', xPos, yPos);

  // Table rows
  yPos += 8;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);

  sanitizedScores.forEach((score, index) => {
    // Check if we need a new page
    if (yPos > pageHeight - 40) {
      pdf.addPage();
      yPos = margin;
    }

    // Alternate row background
    const bgColor = index % 2 === 0 ? 248 : 255;
    pdf.setFillColor(bgColor, bgColor, bgColor);
    pdf.rect(margin, yPos - 4, pageWidth - margin * 2, 7, 'F');

    pdf.setFontSize(9);
    pdf.setTextColor(40, 40, 40);

    xPos = margin + 3;
    
    // Truncate long module names
    const moduleName = score.moduleName.length > 38
      ? score.moduleName.substring(0, 35) + '...'
      : score.moduleName;
    pdf.text(moduleName, xPos, yPos);

    xPos += colWidths.module;
    pdf.text(`${score.score}/${score.maxScore}`, xPos, yPos);

    xPos += colWidths.score;
    pdf.text(`${score.percentage.toFixed(1)}%`, xPos, yPos);

    xPos += colWidths.percentage;
    // Format date safely
    let dateStr = 'N/A';
    try {
      const attemptDate = new Date(score.attemptDate);
      if (!isNaN(attemptDate.getTime())) {
        dateStr = attemptDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: '2-digit',
        });
      }
    } catch {
      dateStr = score.attemptDate || 'N/A';
    }
    pdf.text(dateStr, xPos, yPos);

    xPos += colWidths.date;
    if (score.passed) {
      pdf.setTextColor(0, 150, 0);
      pdf.text('PASSED', xPos, yPos);
    } else {
      pdf.setTextColor(200, 0, 0);
      pdf.text('FAILED', xPos, yPos);
    }

    pdf.setTextColor(40, 40, 40);
    yPos += 7;
  });

  // ============ SUMMARY SECTION ============
  yPos += 10;
  pdf.setDrawColor(0, 100, 180);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPos, pageWidth - margin, yPos);

  yPos += 15;
  pdf.setFontSize(14);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`OVERALL AVERAGE: ${data.overallAverage.toFixed(2)}%`, margin, yPos);

  yPos += 10;
  const passed = data.overallAverage >= 80;
  pdf.setFontSize(16);
  
  if (passed) {
    pdf.setTextColor(0, 150, 0);
    pdf.text('RESULT: PASSED', margin, yPos);
  } else {
    pdf.setTextColor(200, 0, 0);
    pdf.text('RESULT: FAILED', margin, yPos);
  }

  // ============ FOOTER ============
  yPos = pageHeight - 30;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.3);
  pdf.line(margin, yPos, pageWidth - margin, yPos);

  yPos += 8;
  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  pdf.setFont('helvetica', 'normal');
  pdf.text(
    'This is an official academic record generated by Neo Prism Academy.',
    pageWidth / 2,
    yPos,
    { align: 'center' }
  );

  yPos += 5;
  pdf.text(
    `Generated on: ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`,
    pageWidth / 2,
    yPos,
    { align: 'center' }
  );

  // Download the PDF
  const filename = `Marksheet_${data.courseTitle.replace(/\s+/g, '_')}_${data.userName.replace(/\s+/g, '_')}.pdf`;
  pdf.save(filename);
}

export default generateMarksheetPDF;
